import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { z } from 'zod/v3';
import { COLUMN_ACTION_GROUP } from '../../constants';
import { ToolCallModel } from '../tool-call.model';
import { GraphModel } from '@cotera/client/v0/graph/graph.model';
import { Assert } from '@cotera/utilities';
import { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { useState } from 'react';
import {
  ModalPageView,
  toast,
  Loading,
  CollapsibleError,
} from '@cotera/client/app/components';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Label } from '@cotera/client/v0/components/ui/label';
import { Button } from '@cotera/client/v0/components/ui/button';
import React from 'react';
import { err, ok } from 'neverthrow';
import { DG, WF } from '@cotera/sdk/core';
import { relColSchema } from '@cotera/api';
import { findColumnNameConflict } from '../../utils/column-name-validation';

type Payload = {};
type Error = { t: string; message?: string };

type AskParams = {};

export class SaveToolCallColumnAction extends BaseAction<Payload, Error[]> {
  override title = 'Save Column';
  override inputSchema = z.object({});
  shortcut = ['ctrl', 's'];
  group = COLUMN_ACTION_GROUP;
  icon = 'save';

  constructor(
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      pop: () => void;
    },
    private readonly navigator: {
      pushAndReplace: (props: { route: string }) => void;
    }
  ) {
    super();
  }

  override applicable = (context: ApplicableContext): boolean => {
    const model = context.targetedModelOfType(ToolCallModel);
    return model !== null && !model.hasErrors.snapshot();
  };

  override async execute(
    _1: Payload,
    context: ExecuteContext
  ): Promise<ActionResult<Error[]>> {
    // if the column is new, the ask modal will prompt user for a name, which is then set to form state

    const toolCallModel = context.targetedModelOfType(ToolCallModel);
    const graphModel = context.getInScopeModelOfType(GraphModel);

    Assert.assert(toolCallModel !== null && graphModel !== null); // by `applicable()`

    // create the operation
    const formState = toolCallModel.formState.snapshot();

    const datasetName = toolCallModel.datasetName;
    const intendedColName = formState.name;

    // Check for column name conflicts in current dataset and parent lineage
    const conflictDataset = findColumnNameConflict(
      graphModel.graph.snapshot(),
      datasetName,
      intendedColName,
      !toolCallModel.isNew // Exclude current column if it's a rename (not new)
    );

    if (conflictDataset !== null) {
      return err([
        {
          t: 'error',
          message: `A column with the name "${intendedColName}" already exists in dataset "${conflictDataset}"`,
        },
      ]);
    }
    const programEraQL = toolCallModel.programEraQL.snapshot();

    if (programEraQL.isErr()) {
      const errorMessage = JSON.stringify(programEraQL.error);
      toast.error('Failed to compile graph', {
        description: <CollapsibleError error={errorMessage} />,
      });
      return err([
        {
          t: 'error',
          message: errorMessage,
        },
      ]);
    }

    const source = programEraQL.value;

    const tool = formState.tool;

    if (tool === null) {
      throw new Error('tool must be selected');
    }

    const toolName = tool.name.replaceAll('/', '_');

    const toolSymbolMap: WF.ToolSymbolMap = {
      [toolName]: {
        toolName: toolName,
        toolId: tool.id,
      },
    };

    const config: z.infer<typeof relColSchema> = {
      t: 'workflow',
      source,
      toolSymbolMap,
      when: formState.when,
    };

    const operation: DG.OperationData = toolCallModel.isNew
      ? DG.Operations.createColumn({
          name: intendedColName,
          relName: datasetName,
          config,
        })
      : DG.Operations.updateColumn({
          name: intendedColName,
          relName: datasetName,
          config,
        });

    // check to see if it would compile
    const tempCommit: DG.Commit = {
      actorId: 'system',
      timestamp: new Date(),
      operations: [operation],
      message: null,
    };

    const tempGraphCompilationResult = graphModel.compiler
      .snapshot()
      .commit(tempCommit)
      .compile();

    if (tempGraphCompilationResult.isErr()) {
      const errorMessage = tempGraphCompilationResult.error.prettyPrint();
      toast.error('Failed to compile graph', {
        description: <CollapsibleError error={errorMessage} />,
      });
      return err([
        {
          t: 'graph-compilation-error',
          message: errorMessage,
        },
      ]);
    }

    const tempColCompilationResult =
      tempGraphCompilationResult.value.graph[datasetName];

    if (tempColCompilationResult?.isErr()) {
      const errorMessage = tempColCompilationResult.error.prettyPrint();
      toast.error('Failed to compile column', {
        description: <CollapsibleError error={errorMessage} />,
      });
      return err([
        {
          t: 'node-compilation-error',
          message: errorMessage,
        },
      ]);
    }

    const stagingResult = await graphModel.attemptToStageOperations([
      operation,
    ]);

    if (stagingResult.isErr()) {
      const errorMessage = stagingResult.error.prettyPrint();
      toast.error('Failed to stage operations', {
        description: <CollapsibleError error={errorMessage} />,
      });
      return err([
        {
          t: 'staging-error',
          message: errorMessage,
        },
      ]);
    }

    toast.success('Changes saved');

    if (toolCallModel.isNew) {
      this.navigator.pushAndReplace({
        route: `/dataset/${encodeURIComponent(
          datasetName
        )}/column/${toolCallModel.currentNameInGraph.snapshot()}`,
      });
    }

    return ok({});
  }

  shouldAsk = (context: ApplicableContext): boolean => {
    const model = context.targetedModelOfType(ToolCallModel);
    Assert.assert(model !== null); // by `applicable()`
    return model.isNew;
  };

  ask(_: {}, context: ExecuteContext): AskResponse<AskParams> {
    const model = context.targetedModelOfType(ToolCallModel);
    Assert.assert(model !== null);

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => (
            <AskView
              onRespond={async (params) => {
                await onRespond(params);
                this.modalManager.pop();
              }}
              model={model}
            />
          ),
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}

export const AskView: React.FC<{
  onRespond: (params: AskParams) => void;
  model: ToolCallModel;
}> = ({ onRespond, model }) => {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const graph = model.inputs.graph.snapshot();
  const conflictDataset = findColumnNameConflict(
    graph,
    model.datasetName,
    name,
    false
  );

  const validName = conflictDataset === null && name.trim().length > 0;

  return (
    <ModalPageView
      title={`Save ${model.currentNameInGraph.snapshot()}`}
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="tool-call-column-name" className="text-sm">
            Column Name
          </Label>
          {conflictDataset !== null && (
            <span className="text-xs text-destructive">
              A column with this name already exists in dataset "
              {conflictDataset}"
            </span>
          )}
          <Input
            id="tool-call-column-name"
            autoFocus
            placeholder="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="w-full flex justify-end">
          <Button
            type="submit"
            variant="default"
            icon="save"
            disabled={!validName || loading}
            className="w-fit self-end"
            onClick={async () => {
              setLoading(true);
              React.startTransition(() => {
                model.formState.set({
                  ...model.formState.snapshot(),
                  name,
                });
                model.currentNameInGraph.set(name);
              });
              onRespond({ to: name });
              setLoading(false);
            }}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Save'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
